import { DebugLogger } from '@affine/debug';
import type { DocMode } from '@blocksuite/affine/model';
import type { SliceSnapshot } from '@blocksuite/affine/store';
import { Service } from '@toeverything/infra';

import type { DocProperties } from '../../db';
import type { DocsService } from '../../doc';
import type { WorkspaceService } from '../../workspace';
import {
  ACCOUNT_TEMPLATE_CONTENT_VERSION,
  MAX_ACCOUNT_TEMPLATE_BLOB_SIZE,
  MAX_ACCOUNT_TEMPLATE_TOTAL_SIZE,
  MAX_ACCOUNT_TEMPLATES,
} from '../constants';
import type {
  AccountTemplateBlob,
  AccountTemplatePayload,
  PublishResult,
} from '../type';
import {
  collectBlobIds,
  estimatePayloadSize,
  hasCrossDocReferences,
} from '../utils/snapshot';
import type { AccountTemplateService } from './account-template';

const logger = new DebugLogger('AccountTemplateTransferService');

export class TemplateTooLargeError extends Error {
  constructor(readonly size: number) {
    super(`Template is too large: ${size} bytes`);
  }
}

export class TemplateLimitReachedError extends Error {
  constructor(readonly max: number) {
    super(`Shared template limit reached: ${max}`);
  }
}

/**
 * Bridges the account-level template library and the current workspace:
 * publishes a workspace doc into the library, and instantiates a library
 * template as a doc of this workspace.
 */
export class AccountTemplateTransferService extends Service {
  constructor(
    private readonly accountTemplateService: AccountTemplateService,
    private readonly docsService: DocsService,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  /**
   * Read the blobs a snapshot references out of this workspace, so the
   * template stays self-contained. Userspace has no blob channel of its own,
   * hence the size caps.
   */
  private async collectBlobs(snapshot: SliceSnapshot) {
    const collection = this.docsService.getBlocksuiteCollection();
    const blobs = new Map<string, AccountTemplateBlob>();
    let totalSize = 0;
    let skipped = 0;

    for (const blobId of collectBlobIds(snapshot)) {
      try {
        const blob = await collection.blobSync.get(blobId);
        if (!blob) {
          skipped++;
          continue;
        }
        if (
          blob.size > MAX_ACCOUNT_TEMPLATE_BLOB_SIZE ||
          totalSize + blob.size > MAX_ACCOUNT_TEMPLATE_TOTAL_SIZE
        ) {
          skipped++;
          continue;
        }
        blobs.set(blobId, {
          bin: new Uint8Array(await blob.arrayBuffer()),
          mime: blob.type || 'application/octet-stream',
        });
        totalSize += blob.size;
      } catch (e) {
        logger.error('Failed to read blob for account template', {
          blobId,
          error: e,
        });
        skipped++;
      }
    }

    return { blobs, totalSize, skipped };
  }

  private async exportDoc(docId: string) {
    const snapshot = await this.docsService.createSliceSnapshotFromDoc(docId);
    if (!snapshot) {
      throw new Error('Failed to export doc content');
    }

    const { blobs, totalSize, skipped } = await this.collectBlobs(snapshot);

    const payload: AccountTemplatePayload = {
      version: ACCOUNT_TEMPLATE_CONTENT_VERSION,
      root: snapshot,
      blobs: [...blobs.keys()],
    };

    const payloadSize = estimatePayloadSize(payload);
    if (payloadSize + totalSize > MAX_ACCOUNT_TEMPLATE_TOTAL_SIZE) {
      throw new TemplateTooLargeError(payloadSize + totalSize);
    }

    const { doc, release } = this.docsService.open(docId);
    try {
      const properties = doc.getProperties() as Record<string, unknown>;
      // per-workspace ids would dangle in another workspace
      const removed = new Set([
        'id',
        'isTemplate',
        'journal',
        'createdBy',
        'updatedBy',
      ]);
      for (const key of Object.keys(properties)) {
        if (removed.has(key) || key.startsWith('custom:')) {
          delete properties[key];
        }
      }

      return {
        payload,
        blobs,
        skipped,
        contentSize: payloadSize + totalSize,
        title: doc.title$.value || 'Untitled',
        properties,
        primaryMode: doc.primaryMode$.value as DocMode | undefined,
        hasCrossDocReferences: hasCrossDocReferences(snapshot),
      };
    } finally {
      release();
    }
  }

  /** workspace doc -> a new account template */
  async publish(
    docId: string,
    options?: { name?: string }
  ): Promise<PublishResult> {
    if (
      this.accountTemplateService.list.templates$.value.length >=
      MAX_ACCOUNT_TEMPLATES
    ) {
      throw new TemplateLimitReachedError(MAX_ACCOUNT_TEMPLATES);
    }

    const exported = await this.exportDoc(docId);
    const templateId = await this.accountTemplateService.create(
      {
        name: options?.name ?? exported.title,
        primaryMode: exported.primaryMode,
        properties: exported.properties,
        sourceWorkspaceId: this.workspaceService.workspace.id,
        sourceDocId: docId,
        contentSize: exported.contentSize,
        blobCount: exported.blobs.size,
      },
      exported.payload,
      exported.blobs
    );
    return {
      templateId,
      skippedBlobs: exported.skipped,
      hasCrossDocReferences: exported.hasCrossDocReferences,
    };
  }

  /** workspace doc -> an existing account template (content overwritten) */
  async republish(templateId: string, docId: string): Promise<PublishResult> {
    const exported = await this.exportDoc(docId);
    await this.accountTemplateService.replaceContent(
      templateId,
      exported.payload,
      exported.blobs,
      {
        primaryMode: exported.primaryMode,
        properties: exported.properties,
        sourceWorkspaceId: this.workspaceService.workspace.id,
        sourceDocId: docId,
        contentSize: exported.contentSize,
        blobCount: exported.blobs.size,
      }
    );
    return {
      templateId,
      skippedBlobs: exported.skipped,
      hasCrossDocReferences: exported.hasCrossDocReferences,
    };
  }

  /**
   * account template -> a doc of this workspace.
   * @returns the id of the doc the template was applied to
   */
  async applyToDoc(templateId: string, targetDocId?: string): Promise<string> {
    const content = await this.accountTemplateService.loadContent(templateId);
    if (!content) {
      throw new Error('Account template content not found');
    }
    const meta = this.accountTemplateService.list.get(templateId);

    // blob ids are content hashes, so re-adding them here is idempotent
    const collection = this.docsService.getBlocksuiteCollection();
    for (const [blobId, blob] of content.blobs) {
      try {
        await collection.blobSync.set(
          blobId,
          new Blob([blob.bin as unknown as BlobPart], { type: blob.mime })
        );
      } catch (e) {
        logger.error('Failed to restore blob of account template', {
          templateId,
          blobId,
          error: e,
        });
      }
    }

    return this.docsService.duplicateFromSliceSnapshot(content.payload.root, {
      targetDocId,
      properties: meta?.properties as Partial<DocProperties> | undefined,
      primaryMode: meta?.primaryMode,
    });
  }
}
